/**
 * Vorschlag einbringen.
 *
 * Vier Angaben, mehr nicht: Bereich, Kurztitel, Beschreibung, Priorität. Der
 * Kurztitel ist Pflicht, weil die Liste sonst nicht zu überfliegen ist.
 * Bereich und Priorität sind vorbelegt – wer etwas verbessern will, soll nicht
 * erst ein Formular ausfüllen müssen. Ersteller und Datum setzt das System.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { api, ApiFehler } from '../api';
import { useKora } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';

/** Aus welchem Bereich kommt der Nutzer gerade? Der ist vorbelegt. */
const PFAD_BEREICH: Record<string, string> = {
  '/': 'allgemein',
  '/cockpit': 'monitor',
  '/kunden': 'kunden',
  '/auftraege': 'auftraege',
  '/finanzen': 'finanzen',
  '/buchhaltung': 'buchhaltung',
};

const PRIORITAETEN = [
  { wert: 'niedrig', label: 'Niedrig' },
  { wert: 'mittel', label: 'Mittel' },
  { wert: 'hoch', label: 'Hoch' },
];

export function VorschlagModal({
  bereiche,
  onSchliessen,
}: {
  bereiche: { key: string; label: string }[];
  onSchliessen: () => void;
}) {
  const ort = useLocation();
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();

  const [bereich, setBereich] = useState(PFAD_BEREICH[ort.pathname] ?? 'allgemein');
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [prioritaet, setPrioritaet] = useState('mittel');
  const [fehler, setFehler] = useState('');

  const speichern = useMutation({
    mutationFn: () =>
      api.sende<{ id: number; kennung: string }>('/vorschlaege', {
        bereich,
        titel: titel.trim(),
        beschreibung: beschreibung.trim(),
        prioritaet,
      }),
    onSuccess: (a) => {
      void queryClient.invalidateQueries({ queryKey: ['vorschlaege'] });
      void queryClient.invalidateQueries({ queryKey: ['vorschlaege-anzahl'] });
      toastZeigen(`${a.kennung} ist eingebracht. Die Geschäftsführung ist informiert.`);
      onSchliessen();
    },
    onError: (f) =>
      setFehler(f instanceof ApiFehler ? f.message : 'Der Vorschlag konnte nicht gespeichert werden.'),
  });

  function absenden() {
    setFehler('');
    if (!titel.trim()) {
      setFehler('Bitte einen Kurztitel angeben – ohne ihn ist die Liste nicht zu überfliegen.');
      return;
    }
    if (!beschreibung.trim()) {
      setFehler('Bitte beschreiben, worum es geht.');
      return;
    }
    speichern.mutate();
  }

  return (
    <Modal
      titel="Vorschlag einbringen"
      untertitel="Was würde deine Arbeit mit Kora leichter machen?"
      breite={560}
      fest={speichern.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={speichern.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer onClick={absenden} disabled={speichern.isPending}>
            {speichern.isPending ? 'Wird gespeichert …' : 'Einbringen'}
          </KnopfPrimaer>
        </>
      }
    >
      <div className="modal-feldreihe">
        <label className="modal-feld">
          <span>Bereich</span>
          <select
            className="modal-auswahl"
            value={bereich}
            onChange={(e) => setBereich(e.target.value)}
          >
            {bereiche.map((b) => (
              <option key={b.key} value={b.key}>
                {b.label}
              </option>
            ))}
          </select>
        </label>

        <label className="modal-feld">
          <span>Priorität</span>
          <select
            className="modal-auswahl"
            value={prioritaet}
            onChange={(e) => setPrioritaet(e.target.value)}
          >
            {PRIORITAETEN.map((p) => (
              <option key={p.wert} value={p.wert}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="modal-feld">
        <span>Kurztitel</span>
        <input
          className="modal-eingabe"
          value={titel}
          maxLength={90}
          placeholder="Telefonnummer direkt in der Liste anzeigen"
          onChange={(e) => setTitel(e.target.value)}
        />
      </label>

      <label className="modal-feld">
        <span>Beschreibung</span>
        <textarea
          className="modal-textarea"
          value={beschreibung}
          placeholder="Worum geht es, und was wäre danach besser?"
          onChange={(e) => setBeschreibung(e.target.value)}
        />
      </label>

      {fehler && <div className="eh-fehler">{fehler}</div>}
    </Modal>
  );
}